import { useEffect, useMemo, useState } from 'react';
import { VoiceEmotionEditor } from './VoiceEmotionEditor';
import { CreditCostNotice } from '../credits/CreditCostNotice';
import type { VoiceClone } from './VoiceCloneModals';

// =============================================================================
// Item de campanha do tipo "Voz clonada".
//
// O texto é um TEMPLATE: {{primeironome}} & cia. são trocados pelo dado do
// contato no campaignSchedulerService ANTES de virar áudio, então cada contato
// recebe um áudio falando o próprio nome. As marcações de emoção ([excited]…)
// atravessam a substituição intactas, porque não usam a sintaxe {{ }}.
// =============================================================================

// Mesmas chaves suportadas por campaignSchedulerService.processVariables.
const CAMPAIGN_VARIABLES = [
  { label: '{{primeironome}}', token: '{{primeironome}}', sample: 'Maria' },
  { label: '{{nome}}', token: '{{nome}}', sample: 'Maria Silva' },
  { label: '{{telefone}}', token: '{{telefone}}', sample: '47996830995' },
  { label: '{{email}}', token: '{{email}}', sample: 'maria@email.com' },
  { label: '{{categoria}}', token: '{{categoria}}', sample: 'Clientes' },
  { label: '{{observacoes}}', token: '{{observacoes}}', sample: 'observação' },
];

const VARIABLE_PATTERN = /\{\{\s*[^}]+?\s*\}\}/g;

const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

interface Props {
  voices: VoiceClone[];
  voiceCloneId: string;
  text: string;
  onChange: (patch: { voiceCloneId?: string; text?: string }) => void;
}

export function VoiceCloneCampaignItem({ voices, voiceCloneId, text, onChange }: Props) {
  const readyVoices = useMemo(() => voices.filter((voice) => voice.ready), [voices]);
  const [estimate, setEstimate] = useState<{ bytes: number; qty: number; credits: number } | null>(null);

  const hasVariables = VARIABLE_PATTERN.test(text);
  VARIABLE_PATTERN.lastIndex = 0;

  // Para orçar, as variáveis viram valores de exemplo — senão a conta sairia
  // sobre "{{primeironome}}" (16 bytes) em vez de sobre um nome de verdade.
  const sampleText = useMemo(() => {
    let out = text;
    for (const variable of CAMPAIGN_VARIABLES) {
      out = out.split(variable.token).join(variable.sample);
    }
    return out;
  }, [text]);

  useEffect(() => {
    if (!sampleText.trim()) { setEstimate(null); return; }
    let alive = true;
    const timer = window.setTimeout(() => {
      fetch('/api/crm-chat/voice-clones/estimate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() } as HeadersInit,
        body: JSON.stringify({ text: sampleText }),
      })
        .then((response) => response.json())
        .then((data) => { if (alive && data?.success) setEstimate(data.data); })
        .catch(() => { if (alive) setEstimate(null); });
    }, 400);
    return () => { alive = false; window.clearTimeout(timer); };
  }, [sampleText]);

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
        <p className="text-sm font-medium text-emerald-700">🔊 Voz clonada</p>
        <p className="mt-1 text-xs text-emerald-700">
          Cada contato recebe um áudio gerado na voz escolhida, já com as variáveis trocadas pelos dados dele.
        </p>
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">Voz *</label>
        {readyVoices.length === 0 ? (
          <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Nenhuma voz clonada pronta. Crie uma no menu <strong>Clone de Voz</strong>, dentro do Chat CRM.
          </p>
        ) : (
          <select
            value={voiceCloneId}
            onChange={(event) => onChange({ voiceCloneId: event.target.value })}
            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Selecione a voz...</option>
            {readyVoices.map((voice) => (
              <option key={voice.id} value={voice.id}>{voice.name}{voice.isDefault ? ' (padrão)' : ''}</option>
            ))}
          </select>
        )}
      </div>

      <VoiceEmotionEditor
        value={text}
        onChange={(next) => onChange({ text: next })}
        label="Texto do áudio"
        rows={4}
        placeholder="Ex.: [excited] Oi {{primeironome}}! [neutral] Passando para avisar que sua encomenda saiu para entrega."
        variables={CAMPAIGN_VARIABLES.map(({ label, token }) => ({ label, token }))}
      />

      {estimate && estimate.qty > 0 && (
        <CreditCostNotice
          items={[{ featureKey: 'voice_tts_1k', qty: estimate.qty, label: 'mil caracteres' }]}
          note={
            hasVariables
              ? 'Custo POR CONTATO — cada um recebe um áudio próprio. Multiplique pelo número de contatos da campanha.'
              : 'Sem variáveis no texto: o áudio é gerado uma única vez e reaproveitado para todos os contatos, então cobra só esta vez.'
          }
        />
      )}
    </div>
  );
}
